import { AxiosResponse } from "axios";
import { useState } from "react";
import { Redirect, useLocation, useParams } from "react-router";
import BackNav from "../../common/components/BackNav";
import Div from "../../common/components/div";
import { FullPageSpinner } from "../../common/components/FullPageSpinner";
import H1 from "../../common/components/h1";
import { Pill } from "../../common/components/Pill";
import { SmartErrorAlert } from "../../common/components/SmartErrorAlert";
import getLocale from "../../common/functions/getLocale";
import formatMfaChannel from "../../common/functions/formatMfaChannel";
import translate, { TranslationKey } from "../../common/functions/translate";
import { useMfaPageView } from "../../common/hooks/use.analytics";
import useCheckForSession from "../../common/hooks/useCheckForSession";
import { usePageTitle } from "../../common/hooks/usePageTitle";
import { SubmitButton } from "../buttons/Buttons";
import { SecurityCodeMethod, useMfaApi } from "../hooks/useMfaApi";
import { VerifyPassword } from "../VerifyPassword";
import { redirectTo } from "../../common/functions/redirectTo";

interface EditMethodProps {
    type: SecurityCodeMethod
    value: string
    isMultipleMethods: boolean
}

export const RemoveMethod = () => {
    const location = useLocation<EditMethodProps>();
    const { type, value, isMultipleMethods } = location.state || { type: null };
    const [isLoading, setIsLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState<AxiosResponse<any> | null>(null);
    const [isVerified, setIsVerified] = useState(true);
    const api = useMfaApi();
    const { uuid } = useParams<{uuid: string}>();
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    useCheckForSession(setIsCheckingSession);
    usePageTitle(translate('mfa_title_edit'));
    useMfaPageView('edit', `remove ${type}`);

    const accountSettingsUrl = `/my/account-settings?updated_mfa_method=${uuid}&lang=${getLocale()}`;

    if (!type) {
        return <Redirect to={accountSettingsUrl}/>
    }

    async function onSubmit(event?: React.FormEvent<HTMLFormElement>) {
        event?.preventDefault();
        setIsLoading(true);
        setApiResponse(null);
        setIsVerified(true);
        
        const removeMethodResponse = await api.mfaDelete(uuid);
        if (removeMethodResponse?.status === 401) {
            setIsVerified(false);
        }
        if (removeMethodResponse?.status !== 204) {
            setApiResponse(removeMethodResponse);
            setIsLoading(false);
            return;
        }
        redirectTo(accountSettingsUrl, 'Successfully removed method.');
    }

    if (isCheckingSession) {
        return <FullPageSpinner />
    }
    if (!isVerified) {
        // do we need new content for flow=remove?
        return <VerifyPassword type={type} flow={'add'} onVerified={onSubmit}/>;
    }
    let bodyTextKey: TranslationKey = `remove_text_${type}_disable`;
    let buttonTextKey: TranslationKey = 'disable_two_step_verification';
    if (isMultipleMethods) {
        bodyTextKey = `remove_text_${type}`;
        buttonTextKey = `remove_button_${type}`;
    }

    const headingTitle = translate(`remove_this_${type}`);
    return (<>
        <BackNav />
        <Div isContainer paddingy={1} paddingyMD={2}>
            <form onSubmit={onSubmit} method="POST">
                <Div isRow>
                    <Div cols={12}>
                        <H1>
                            {headingTitle}
                        </H1>
                    </Div>
                </Div>
                <SmartErrorAlert
                    name='MfaDeleteMethod'
                    fieldRef={null}
                    apiResponse={apiResponse}
                    conversion={null}
                    invalidFields={null}
                    onInvalidField={() => {}}
                    parentHeading={headingTitle}
                />
                <Div isRow>
                    <Div cols={12} colsMD={8}>
                        <p className="ds-u-margin-bottom--3 ds-u-margin-top--3 ds-text-heading--md ds-u-font-weight--bold">
                            {translate(`current_mfa_${type}`)}
                        </p>
                        <Pill>{formatMfaChannel(value)}</Pill>
                        <p className={'ds-u-margin-bottom--3 ds-u-margin-top--3'}>{translate(bodyTextKey)}</p>
                        <SubmitButton isLoading={isLoading} mfaAnalyticsData={{
                            text: translate(buttonTextKey),
                            parentComponentHeading: headingTitle,
                        }}>
                            {translate(buttonTextKey)}
                        </SubmitButton>
                    </Div>
                </Div>
            </form>
        </Div>
    </>);
}